/**
 * Vault operations for staking module.
 *
 * @remarks
 * This file contains all vault-related operations for the staking module,
 * including vault information retrieval, transaction history, and general vault utilities.
 * It also contains the internal implementation functions used by other modules.
 *
 * @internal
 * @packageDocumentation
 */
/**
 * Returns vault shares for an asset amount using the vault contract `convertToShares` view.
 *
 * @internal
 */
export async function _convertVaultAssetsToShares(stakewiseSDK, logger, vaultAddress, assets) {
    logger.debug("Calling vault convertToShares", {
        vaultAddress,
        assets: assets.toString(),
    });
    const vaultContract = stakewiseSDK.contracts.helpers.createVault({
        vaultAddress,
        options: { chainId: stakewiseSDK.network },
    });
    const shares = await vaultContract.convertToShares(assets);
    logger.debug("vault convertToShares result", {
        vaultAddress,
        shares: shares.toString(),
    });
    return shares;
}
/**
 * Retrieves comprehensive vault information and configuration.
 *
 * @remarks
 * Returns the StakeWise vault's configuration and performance
 * metrics (APY, capacity, fees, token info, etc.).
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance for vault operations
 * @param logger - Logger instance for debugging and monitoring
 * @param vaultAddress - Vault address
 * @returns Promise resolving to comprehensive vault information
 * @throws {Error} When vault address is not found or not configured
 * @throws {Error} When vault information cannot be retrieved from StakeWise
 *
 * @internal
 */
export async function _getVault(stakewiseSDK, logger, vaultAddress) {
    logger.debug("Retrieving vault data from StakeWise SDK", {
        vaultAddress,
    });
    const vault = await stakewiseSDK.vault.getVault({ vaultAddress });
    logger.debug("Successfully retrieved vault data from StakeWise SDK", {
        apy: vault.apy,
        capacity: vault.capacity,
        totalAssets: vault.totalAssets?.toString(),
        tokenName: vault.tokenName,
        tokenSymbol: vault.tokenSymbol,
    });
    return {
        vaultAddress,
        apy: vault.apy,
        baseApy: vault.baseApy,
        capacity: vault.capacity,
        totalAssets: vault.totalAssets,
        performance: vault.performance,
        feePercent: vault.feePercent,
        lastFeePercent: vault.lastFeePercent,
        lastFeeUpdateTimestamp: vault.lastFeeUpdateTimestamp,
        tokenName: vault.tokenName,
        tokenSymbol: vault.tokenSymbol,
        isErc20: vault.isErc20,
        isPrivate: vault.isPrivate,
        isGenesis: vault.isGenesis,
        feeRecipient: vault.feeRecipient,
        validatorsManager: vault.validatorsManager,
        description: vault.description,
        displayName: vault.displayName,
        imageUrl: vault.imageUrl,
        version: vault.version,
        createdAt: vault.createdAt,
        depositDataManager: vault.depositDataManager ?? "",
        // Add missing required properties with default values
        vaultAdmin: vault.vaultAdmin ?? "",
        mevRecipient: vault.mevRecipient ?? "",
        blocklistCount: vault.blocklistCount ?? 0,
        whitelistCount: vault.whitelistCount ?? 0,
        isSmoothingPool: vault.isSmoothingPool ?? false,
        whitelistManager: vault.whitelistManager ?? "",
        allocatorMaxBoostApy: vault.allocatorMaxBoostApy ?? 0,
        osTokenConfig: vault.osTokenConfig ?? {
            ltvPercent: "0",
            liqThresholdPercent: "0",
        },
        isBlocklist: vault.isBlocklist ?? false,
        queuedShares: vault.queuedShares ?? "0",
        depositDataRoot: vault.depositDataRoot ?? null,
        isCollateralized: vault.isCollateralized ?? false,
        blocklistManager: vault.blocklistManager ?? "",
    };
}
//# sourceMappingURL=data:application/json;base64,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